import { useCallback } from 'react'
import {
	DndContext,
	DragEndEvent,
	MouseSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, AlertTriangle } from 'lucide-react'

import { Button } from '@/common/ui/button'
import { Alert, AlertDescription } from '@/common/ui/alert'

import { ChatMessageType } from '@/schema'

import { MessageContentEditor } from '../MessageContentEditor'
import { MAX_MESSAGES_PER_RULE } from '../../types'
import type { MessageContentInput } from '../../types'

interface MessageListEditorProps {
	messages: MessageContentInput[]
	onChange: (messages: MessageContentInput[]) => void
	maxMessages?: number
}

export function MessageListEditor({
	messages,
	onChange,
	maxMessages = MAX_MESSAGES_PER_RULE,
}: MessageListEditorProps) {
	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 200,
				tolerance: 5,
			},
		}),
	)

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			if (!over || active.id === over.id) return

			const oldIndex = messages.findIndex((m) => getMessageId(m) === active.id)
			const newIndex = messages.findIndex((m) => getMessageId(m) === over.id)

			const newMessages = arrayMove(messages, oldIndex, newIndex).map(
				(msg, index) => ({
					...msg,
					order: index,
				}),
			)

			onChange(newMessages)
		},
		[messages, onChange],
	)

	const handleAddMessage = useCallback(() => {
		if (messages.length >= maxMessages) return

		const newMessage: MessageContentInput = {
			id: crypto.randomUUID(),
			order: messages.length,
			messageType: ChatMessageType.TEXT,
		}

		onChange([...messages, newMessage])
	}, [messages, maxMessages, onChange])

	const handleUpdateMessage = useCallback(
		(index: number, updatedMessage: MessageContentInput) => {
			const newMessages = [...messages]
			newMessages[index] = updatedMessage
			onChange(newMessages)
		},
		[messages, onChange],
	)

	const handleDeleteMessage = useCallback(
		(index: number) => {
			const newMessages = messages
				.filter((_, i) => i !== index)
				.map((msg, i) => ({ ...msg, order: i }))
			onChange(newMessages)
		},
		[messages, onChange],
	)

	const isAtMax = messages.length >= maxMessages
	const isNearMax = messages.length >= maxMessages - 1

	return (
		<div className="space-y-4">
			{isNearMax && (
				<Alert
					variant="default"
					className="border-yellow-500/50 bg-yellow-500/10"
				>
					<AlertTriangle className="h-4 w-4 text-yellow-600" />
					<AlertDescription className="text-yellow-700">
						LINE รองรับการส่งข้อความสูงสุด {maxMessages} ข้อความต่อครั้ง
						{isAtMax
							? ' (ถึงขีดจำกัดแล้ว)'
							: ` (เหลืออีก ${maxMessages - messages.length} ข้อความ)`}
					</AlertDescription>
				</Alert>
			)}

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={messages.map((m) => getMessageId(m))}
					strategy={verticalListSortingStrategy}
				>
					<div className="space-y-3">
						{messages.map((message, index) => (
							<SortableMessageItem
								key={getMessageId(message)}
								message={message}
								index={index}
								onChange={(updated) => handleUpdateMessage(index, updated)}
								onDelete={() => handleDeleteMessage(index)}
							/>
						))}
					</div>
				</SortableContext>
			</DndContext>

			{messages.length === 0 && (
				<div className="rounded-lg border border-dashed p-8 text-center">
					<p className="text-sm text-muted-foreground">
						ยังไม่มีข้อความ คลิกปุ่มด้านล่างเพื่อเพิ่ม
					</p>
				</div>
			)}

			<Button
				type="button"
				variant="outline"
				className="w-full"
				onClick={handleAddMessage}
				disabled={isAtMax}
			>
				<Plus className="mr-2 h-4 w-4" />
				เพิ่มข้อความ
				{isAtMax && ' (ถึงขีดจำกัดแล้ว)'}
			</Button>
		</div>
	)
}

// ==================== Sortable Message Item ====================

interface SortableMessageItemProps {
	message: MessageContentInput
	index: number
	onChange: (message: MessageContentInput) => void
	onDelete: () => void
}

function SortableMessageItem({
	message,
	index,
	onChange,
	onDelete,
}: SortableMessageItemProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: getMessageId(message) })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	return (
		<div ref={setNodeRef} style={style}>
			<MessageContentEditor
				value={message}
				onChange={onChange}
				onDelete={onDelete}
				index={index}
				isDragging={isDragging}
				dragHandleProps={{ ...attributes, ...listeners }}
			/>
		</div>
	)
}

// ==================== Helpers ====================

function getMessageId(message: MessageContentInput): string {
	return message.id || `temp-${message.order}`
}
